import React, { useEffect, useRef, useState } from 'react';
import VoxelDataTableHeadCell from '../../../voxel/DataTable/VoxelDataTableHeadCell';
import { VoxelPopover } from '../../../voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../voxel/Popover/VoxelPopoverTrigger';
import FilterChangeIcon from '../../../voxel/Icons/FilterChangeIcon';
import { HeadCellImplementationProps } from '../KeywordTable/HeadCells/models';
import { useThrottleValue } from '../../../../logic/otherUtilities/useThrottleValue';
import { usersApiService } from '../../../../graphql/users/usersApiService';
import { FrontendUserEventGq } from '../../../../graphql/users/queries/useFrontendLogUserEventGq';

export interface HeadCellProps extends HeadCellImplementationProps {
  hasFilter: boolean;
  align?: 'left' | 'right' | 'center';
}

const HeadCell: React.FC<HeadCellProps> = ({
  children,
  hasFilter,
  align = 'right',
  basicConfig,
  basicState,
}) => {
  // State calculation
  const hasChange = hasFilter || !!basicState.sortDirectionState;

  // Grabbing
  const ref = useRef<HTMLDivElement>(null);
  const [grabbing, setGrabbing] = useState(false);
  const [xCoordinate, setXCoordinate] = useState<number>();

  // Grab active logic
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  useEffect(() => {
    if (!grabbing) return;

    // Document feature usage
    logUserEventMutation({
      event: FrontendUserEventGq.TABLE_QUICK_RESIZE_COLUMN,
    });

    // Track mouse movement
    const coordinateHandler = (event: MouseEvent) => setXCoordinate(event.pageX);
    window.addEventListener('mousemove', coordinateHandler, false);

    // Stop tracking mousemovement upon mouseup
    const stopGrabHandler = () => {
      setGrabbing(false);
      setXCoordinate(undefined);
    };
    window.addEventListener('mouseup', stopGrabHandler, false);

    // Remove event listeners if component is unmounted
    return () => {
      window.removeEventListener('mouseup', stopGrabHandler);
      window.removeEventListener('mousemove', coordinateHandler);
    };
  }, [grabbing]);

  // Update column width
  const throttledCoordinates = useThrottleValue(xCoordinate, 10);
  useEffect(() => {
    if (grabbing && !!basicState?.setColumnWidth && !!xCoordinate) {
      const proposedWidth =
        xCoordinate -
        ref.current.offsetLeft +
        basicState.tableContainerRef?.scrollLeft -
        basicState.tableContainerRef?.offsetLeft;
      const maxWidth = 400;
      const minWidth = 120;
      const constrainedWidth = Math.min(Math.max(minWidth, proposedWidth), maxWidth);
      basicState.setColumnWidth(constrainedWidth);
    }
  }, [grabbing, throttledCoordinates]);

  if (!basicState.showColumn) {
    return null;
  }

  return (
    <VoxelDataTableHeadCell
      ref={basicState?.setColumnRef}
      align={align}
      colSpan={1}
      className={`${basicConfig.className} ${grabbing && '!bg-voxel-gray-200'}  group/head`}
    >
      <div className="relative" ref={ref}>
        <VoxelPopover
          fixed
          enableArrow
          placement={align === 'right' ? 'top-end' : align === 'center' ? 'top' : 'top-start'}
        >
          <VoxelPopoverTrigger>
            <FilterChangeIcon
              tooltip={basicConfig.tooltip}
              align={align}
              title={basicConfig.title}
              hasChange={hasChange}
            />
          </VoxelPopoverTrigger>
          <VoxelPopoverContent>{children}</VoxelPopoverContent>
        </VoxelPopover>
        {!!basicState.setColumnWidth && !basicConfig.disableResize && (
          <div
            className={`
            absolute -right-[20px] -top-[2px]
            px-2 
            group-hover/head:opacity-100
            ${!grabbing && ' opacity-0 '}
            cursor-ew-resize
          `}
            onMouseDown={() => setGrabbing(true)}
          >
            <div
              className={`
              ${!grabbing && ' bg-gray-300 '}
              ${grabbing && ' bg-primary-400 '}
              w-[3px] h-[22px]
            `}
            />
          </div>
        )}
      </div>
    </VoxelDataTableHeadCell>
  );
};

export default HeadCell;
